'use client'

import { LoadingIndicator } from '~/components/LoadingIndicator'
import { AccountActionButtons } from '~/components/account-actions/AccountActionButtons'
import { LargeCurrencySmallCents } from '~/components/account-detail/LargeCurrencySmallCents'
import { PageLayout, Skeleton } from '~/components/component-library'
import { SegmentedAssetsBar } from '~/components/component-library/SegmentedAssetsBar'
import { PostOnboardingChecklistPanel } from '~/components/post-onboarding-checklist/PostOnboardingChecklistPanel'
import { PostOnboardingChecklistView } from '~/components/post-onboarding-checklist/PostOnboardingChecklistView'
import { shouldShowChecklistPanel } from '~/components/post-onboarding-checklist/utils'
import { IdleUsdcMessage } from '~/components/smart-accounts/SmartAccountsList/IdleUsdcMessage'
import { PasskeySetupMessage } from '~/components/smart-accounts/SmartAccountsList/PasskeySetupMessage'
import { useAssetSegments } from '~/components/smart-accounts/SmartAccountsList/useAssetSegments'
import { TransactionFeed } from '~/components/smart-accounts/TransactionFeed'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useIsGettingStarted } from '~/hooks/useIsGettingStarted'
import { usePostOnboardingChecklist } from '~/hooks/usePostOnboardingChecklist'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  useCanCreateAnyTransaction,
  useTransactions,
} from '~/hooks/useTransactions'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { useSmartAccountsShowSmallBalances } from '~/hooks/utils'
import { OrgSmartAccount } from '~/types'
import { TokenBalance } from '~/utils/account'
import { useIsMobile } from '~/utils/devices'

export const SmartAccountsList = () => {
  const { smartAccounts: orderedSmartAccounts } = useSmartAccounts({
    orderedByValue: true,
  })
  const activeOrgId = useActiveOrgId()

  const {
    balances,
    totalValue,
    isLoading: isBalancesLoading,
  } = useOrgBalances(activeOrgId!)

  const { showSmallBalances } = useSmartAccountsShowSmallBalances()

  const canShowAssetsBar = totalValue > 0
  const { org } = useActiveOrg()

  const { isInitialLoading } = useTransactions({ hideDust: true })

  // get treasury address
  const treasuryAccount = orderedSmartAccounts.find(
    (account) => account.role === 'TREASURY',
  )
  const treasuryAddress = treasuryAccount?.address

  const { isGettingStarted: showGettingStartedState, isSettled } =
    useIsGettingStarted()
  const isMobile = useIsMobile()
  const { checklist } = usePostOnboardingChecklist()
  const showChecklistPanel = shouldShowChecklistPanel(checklist)

  // Without a passkey a member can't sign or be added to accounts as a signer.
  const { passkeys, isLoading: isPasskeysLoading } = useUserPasskeys()
  const passkeySetupMessage =
    !isPasskeysLoading && passkeys.length === 0 ? (
      <PasskeySetupMessage />
    ) : undefined

  // isSettled covers the checklist too: without it the normal dashboard
  // renders for a moment before the welcome state replaces it.
  const showLoadingIndicator =
    !treasuryAddress || isInitialLoading || !org || !isSettled

  if (showLoadingIndicator) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoadingIndicator fullScreen={false} />
      </div>
    )
  }

  if (showGettingStartedState && treasuryAccount && checklist) {
    return (
      <PageLayout header={passkeySetupMessage}>
        <PostOnboardingChecklistView
          checklist={checklist}
          treasuryAccount={treasuryAccount}
        />
      </PageLayout>
    )
  }

  // Funded but not done (or just finished): the checklist moves to the right
  // sidebar until it completes or this member dismisses it.
  const checklistPanel =
    showChecklistPanel && checklist && treasuryAccount ? (
      <PostOnboardingChecklistPanel checklist={checklist} />
    ) : undefined

  return (
    <PageLayout
      // The layout widens the page for a sidebar it does not render on
      // mobile, so the slot has to be empty there rather than merely hidden.
      rightSidebar={isMobile ? undefined : checklistPanel}
      header={passkeySetupMessage}
      title={
        isBalancesLoading ? (
          <div className="flex h-[75px] flex-row justify-start space-x-2 pt-3">
            <Skeleton className="h-14 w-16" />
            <Skeleton className="h-8 w-8" />
          </div>
        ) : (
          <LargeCurrencySmallCents amount={totalValue} />
        )
      }
    >
      {/* Match the action row's right inset (md:pr-8) so the bar and banner line up with it. */}
      <div className="space-y-6 md:pr-8">
        {isBalancesLoading ? (
          <div className="pb-4">
            <Skeleton className="h-4 max-w-lg" />
          </div>
        ) : canShowAssetsBar ? (
          <DashboardAssetsBar
            smartAccounts={orderedSmartAccounts}
            balances={balances}
            showSmallBalances={showSmallBalances}
          />
        ) : (
          <div className="h-8 max-w-lg" />
        )}
        {!isBalancesLoading && (
          <IdleUsdcMessage
            accounts={orderedSmartAccounts}
            balances={balances}
            treasuryAddress={treasuryAddress}
          />
        )}
      </div>
      <DashboardToolbar />
      {isMobile && checklistPanel}
      <TransactionFeed />
    </PageLayout>
  )
}

const DashboardToolbar = () => {
  const canCreateAnyTransaction = useCanCreateAnyTransaction()

  // No account preselected on the dashboard, so each flow asks which to use.
  return (
    <AccountActionButtons
      isDisabled={!canCreateAnyTransaction}
      disabledReason="You're not a signer on any accounts"
    />
  )
}

function DashboardAssetsBar({
  smartAccounts,
  balances,
  showSmallBalances,
}: {
  smartAccounts: OrgSmartAccount[]
  balances: Record<string, { balances: TokenBalance[]; totalValue: number }>
  showSmallBalances: boolean
}) {
  const { segments, totalValue } = useAssetSegments(
    smartAccounts,
    balances,
    showSmallBalances,
  )

  return (
    <SegmentedAssetsBar
      segments={segments}
      totalValue={totalValue}
      smartAccounts={smartAccounts}
    />
  )
}
